import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

/** ---------- Toast (no external lib) ---------- */
function ToastStack({ toasts, onRemove }) {
  const stackRef = useRef(null);

  useEffect(() => {
    if (!stackRef.current) return;
    gsap.fromTo(
      stackRef.current.querySelectorAll(".toast-item"),
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.2, stagger: 0.03, ease: "power2.out" }
    );
  }, [toasts]);

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[9999] w-[min(92vw,380px)]">
      <div ref={stackRef} className="flex flex-col gap-2">
        {toasts.map((t) => {
          const styles =
            t.type === "success"
              ? "border-green-200 bg-green-50 text-green-900"
              : t.type === "error"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-blue-200 bg-blue-50 text-blue-900";

          return (
            <div
              key={t.id}
              className={[
                "toast-item pointer-events-auto rounded-xl border px-4 py-3 shadow-sm",
                styles,
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  {t.title && <div className="font-semibold">{t.title}</div>}
                  <div className="mt-0.5">{t.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(t.id)}
                  className="rounded-lg border bg-white/60 px-2 py-1 text-xs font-semibold hover:bg-white"
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** ---------- Confirm Modal ---------- */
function ConfirmModal({ open, title, description, confirmText, cancelText, onConfirm, onCancel }) {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open || !modalRef.current) return;
    gsap.fromTo(
      modalRef.current,
      { opacity: 0, y: 12, scale: 0.98 },
      { opacity: 1, y: 0, scale: 1, duration: 0.22, ease: "power2.out" }
    );
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div ref={modalRef} className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="border-b px-5 py-4">
          <div className="text-lg font-semibold text-gray-900">{title}</div>
          {description && <div className="mt-1 text-sm text-gray-600">{description}</div>}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          >
            {cancelText || "Cancel"}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 active:translate-y-[1px]"
          >
            {confirmText || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** ---------- Safe JSON parse (handles text responses) ---------- */
async function safeJson(response) {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { status: "error", message: "Invalid JSON response", raw: text };
  }
}

/** ---------- Discount Form Modal ---------- */
function DiscountFormModal({ open, product, submitting, onClose, onSubmit, toast }) {
  const modalRef = useRef(null);

  const [selectedSize, setSelectedSize] = useState("");
  const [minQuantity, setMinQuantity] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedSize("");
    setMinQuantity("");
    setDiscountedPrice("");
    setEndDate("");
  }, [open, product?.product_id]);

  useEffect(() => {
    if (!open || !modalRef.current) return;
    gsap.fromTo(
      modalRef.current,
      { opacity: 0, y: 12, scale: 0.98 },
      { opacity: 1, y: 0, scale: 1, duration: 0.22, ease: "power2.out" }
    );
  }, [open]);

  const validate = () => {
    if (!product?.product_id) return "Please select a product.";
    if (!selectedSize) return "Please select a size.";
    if (!minQuantity) return "Minimum quantity is required.";
    if (!discountedPrice) return "Discounted price is required.";
    if (!endDate) return "End date is required.";
    return "";
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9997] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={modalRef} className="w-full max-w-lg rounded-2xl bg-white shadow-xl overflow-hidden">
        <div className="border-b px-5 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-gray-900">Apply Discount</div>
            <div className="mt-1 text-sm text-gray-600 truncate">
              {product?.name || `Product ID: ${product?.product_id || ""}`}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border px-3 py-1.5 text-sm font-semibold hover:bg-gray-50"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const err = validate();
            if (err) {
              toast("error", err);
              return;
            }

            onSubmit({
              product_id: Number(product.product_id),
              size: selectedSize,
              min_quantity: Number(minQuantity),
              discounted_price: Number(discountedPrice),
              end_date: endDate,
            });
          }}
          className="p-5 space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700">Size</label>
              <select
                value={selectedSize}
                onChange={(e) => setSelectedSize(e.target.value)}
                className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Select Size</option>
                {(product?.sizes || []).map((s, idx) => (
                  <option key={idx} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700">Minimum Quantity</label>
              <input
                value={minQuantity}
                onChange={(e) => setMinQuantity(e.target.value)}
                type="number"
                className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="e.g. 5"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700">Discounted Price</label>
              <input
                value={discountedPrice}
                onChange={(e) => setDiscountedPrice(e.target.value)}
                type="number"
                className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="e.g. 199"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700">End Date</label>
              <input
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                type="date"
                className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>

          <div className="pt-2 flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={submitting}
              className={[
                "w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition active:translate-y-[1px]",
                submitting ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700",
              ].join(" ")}
            >
              {submitting ? "Saving..." : "Apply Discount"}
            </button>

            <button
              type="button"
              onClick={() => {
                setSelectedSize("");
                setMinQuantity("");
                setDiscountedPrice("");
                setEndDate("");
                toast("info", "Form reset.");
              }}
              className="w-full rounded-xl border px-4 py-2.5 text-sm font-semibold hover:bg-gray-50"
            >
              Reset
            </button>
          </div>

          <div className="text-xs text-gray-500">Tip: Click outside the popup to close.</div>
        </form>
      </div>
    </div>
  );
}

function ManageDiscounts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [showAllDiscounts, setShowAllDiscounts] = useState({});
  const [toasts, setToasts] = useState([]);

  const [confirmState, setConfirmState] = useState({
    open: false,
    title: "",
    description: "",
    action: null,
  });

  const [formOpen, setFormOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  const wrapRef = useRef(null);

  const toast = (type, message, title) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((prev) => [...prev, { id, type, message, title }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 2800);
  };

  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const openConfirm = ({ title, description, action }) => {
    setConfirmState({ open: true, title, description, action });
  };
  const closeConfirm = () => setConfirmState({ open: false, title: "", description: "", action: null });

  /** Ensure backend fields are always arrays so `[0]` / `[idx]` never becomes undefined */
  const asArray = (v) => {
    if (Array.isArray(v)) return v;
    if (v === null || v === undefined || v === "") return [];
    return [v];
  };

  const normalizeProduct = (p) => {
    const product_id = p.product_id ?? p.id ?? p.productId;

    return {
      ...p,
      product_id,
      sizes: asArray(p.sizes),
      min_quantity: asArray(p.min_quantity),
      discounted_price: asArray(p.discounted_price),
      end_date: asArray(p.end_date),
      product_price_id: asArray(p.product_price_id), // optional if your API provides it
    };
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${ApiURl}/getDiscounts.php`);
      const json = await safeJson(res);

      const list = Array.isArray(json?.products) ? json.products : [];
      const normalized = list.map(normalizeProduct);

      setProducts(normalized);
      setPage(1);
    } catch (e) {
      console.error(e);
      toast("error", "Failed to load products/discounts.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".md-anim",
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.06, ease: "power2.out" }
      );
    }, wrapRef);
    return () => ctx.revert();
  }, []);

  const selectedProduct = useMemo(
    () => products.find((p) => String(p.product_id) === String(selectedProductId)),
    [products, selectedProductId]
  );

  const handleProductClick = (productId) => {
    setSelectedProductId(productId);
    setFormOpen(true);
  };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    toast("info", "Saving discount...");

    try {
      const res = await fetch(`${ApiURl}/update_discount.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await safeJson(res);

      if (json?.status === "success") {
        toast("success", json?.message || "Discount added/updated successfully!");
        setFormOpen(false);
        await fetchProducts();
      } else {
        toast("error", json?.message || "Error adding/updating discount.");
      }
    } catch (error) {
      console.error(error);
      toast("error", "An error occurred while processing the discount.");
    } finally {
      setSubmitting(false);
    }
  };

  /** FIXED: guarantee min_quantity is present and numeric */
  const handleRemoveDiscount = async (productId, ruleIndex, minQty, size) => {
    const pid = Number(productId);
    const mq = Number(minQty);

    if (!Number.isFinite(pid) || !Number.isFinite(mq)) {
      toast("error", "Missing min quantity/product id for this rule. Please refresh and try again.");
      return;
    }

    openConfirm({
      title: "Remove discount?",
      description: `This will remove the discount for product ID ${pid} (min qty: ${mq}).`,
      action: async () => {
        closeConfirm();
        toast("info", "Removing discount...");

        try {
          const res = await fetch(`${ApiURl}/remove_discount.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              product_id: pid,
              min_quantity: mq,

              // optional (safe to keep even if backend ignores)
              product_price_id: Number(ruleIndex),
              size: size || "",
            }),
          });

          const json = await safeJson(res);

          if (json?.status === "success") {
            toast("success", json?.message || "Discount removed successfully!");
            await fetchProducts();
          } else {
            toast("error", json?.message || "Error removing discount.");
          }
        } catch (error) {
          console.error(error);
          toast("error", "An error occurred while removing the discount.");
        }
      },
    });
  };

  const toggleShowAll = (productId) => {
    setShowAllDiscounts((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  /** ---------- Pagination derived values ---------- */
  const totalPages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const paginatedProducts = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return products.slice(start, start + PAGE_SIZE);
  }, [products, safePage]);

  const Pagination = () => (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t bg-white">
      <div className="text-xs text-gray-600">
        Showing{" "}
        <span className="font-semibold">{products.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}</span>{" "}
        to{" "}
        <span className="font-semibold">{Math.min(safePage * PAGE_SIZE, products.length)}</span>{" "}
        of <span className="font-semibold">{products.length}</span> products
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPage(1)}
          disabled={safePage === 1}
          className="rounded-lg border px-3 py-2 text-xs font-semibold disabled:opacity-50 hover:bg-gray-50"
        >
          First
        </button>
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={safePage === 1}
          className="rounded-lg border px-3 py-2 text-xs font-semibold disabled:opacity-50 hover:bg-gray-50"
        >
          Prev
        </button>

        <div className="px-2 text-xs text-gray-700">
          Page <span className="font-semibold">{safePage}</span> of{" "}
          <span className="font-semibold">{totalPages}</span>
        </div>

        <button
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={safePage === totalPages}
          className="rounded-lg border px-3 py-2 text-xs font-semibold disabled:opacity-50 hover:bg-gray-50"
        >
          Next
        </button>
        <button
          type="button"
          onClick={() => setPage(totalPages)}
          disabled={safePage === totalPages}
          className="rounded-lg border px-3 py-2 text-xs font-semibold disabled:opacity-50 hover:bg-gray-50"
        >
          Last
        </button>
      </div>
    </div>
  );

  return (
    <div ref={wrapRef} className="w-full">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        description={confirmState.description}
        confirmText="Remove"
        cancelText="Cancel"
        onCancel={closeConfirm}
        onConfirm={async () => {
          if (typeof confirmState.action === "function") await confirmState.action();
        }}
      />

      <DiscountFormModal
        open={formOpen}
        product={selectedProduct}
        submitting={submitting}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        toast={toast}
      />

      <div className="md-anim rounded-2xl border bg-white p-4 sm:p-6 lg:p-8">
        <div className="md-anim flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Manage Discounts</h2>
            <p className="mt-1 text-sm text-gray-600">
              Click <span className="font-semibold">Select</span> to open the discount form popup.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchProducts}
            className="w-full sm:w-auto rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 active:translate-y-[1px]"
          >
            Refresh
          </button>
        </div>

        <div className="md-anim mt-6 rounded-2xl border bg-white">
          <div className="border-b p-4">
            <div className="text-sm font-semibold text-gray-900">Products</div>
            <div className="mt-1 text-xs text-gray-500">Showing 10 per page with pagination.</div>
          </div>

          {/* ✅ TABLE VIEW FOR BOTH DESKTOP + MOBILE (scrollable on mobile) */}
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full text-left">
              <thead className="bg-gray-50 text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="px-3 py-3 sm:px-4">Product</th>
                  <th className="px-3 py-3 sm:px-4 whitespace-nowrap">Min Qty</th>
                  <th className="px-3 py-3 sm:px-4 whitespace-nowrap">Discount</th>
                  <th className="px-3 py-3 sm:px-4">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y text-[12px] sm:text-sm">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-600">
                      Loading...
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-600">
                      No products found.
                    </td>
                  </tr>
                ) : (
                  paginatedProducts.map((p) => {
                    const hasDiscount = Array.isArray(p.discounted_price) && p.discounted_price.length > 0;
                    const showAll = !!showAllDiscounts[p.product_id];

                    return (
                      <React.Fragment key={p.product_id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-3 sm:px-4 font-medium text-gray-900">{p.name}</td>
                          <td className="px-3 py-3 sm:px-4 whitespace-nowrap">
                            {hasDiscount ? (p.min_quantity?.[0] ?? "—") : "—"}
                          </td>
                          <td className="px-3 py-3 sm:px-4 whitespace-nowrap">
                            {hasDiscount ? `₹${p.discounted_price?.[0]}` : "No Discount"}
                          </td>
                          <td className="px-3 py-3 sm:px-4">
                            <div className="flex flex-wrap gap-2 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => handleProductClick(p.product_id)}
                                className="rounded-xl bg-blue-600 px-3 py-2 text-[11px] sm:text-xs font-semibold text-white hover:bg-blue-700 active:translate-y-[1px]"
                              >
                                Select
                              </button>

                              {hasDiscount && !showAll && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleRemoveDiscount(p.product_id, 0, p.min_quantity?.[0], p.sizes?.[0] || "")
                                  }
                                  className="rounded-xl bg-red-600 px-3 py-2 text-[11px] sm:text-xs font-semibold text-white hover:bg-red-700"
                                >
                                  Remove
                                </button>
                              )}

                              {hasDiscount && p.discounted_price.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => toggleShowAll(p.product_id)}
                                  className="rounded-xl bg-green-600 px-3 py-2 text-[11px] sm:text-xs font-semibold text-white hover:bg-green-700"
                                >
                                  {showAll ? "Show Less" : "Show More"}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {showAll && hasDiscount && (
                          <tr className="bg-gray-50">
                            <td colSpan={4} className="px-3 py-4 sm:px-4">
                              <div className="grid gap-3 sm:grid-cols-2">
                                {p.discounted_price.map((price, idx) => (
                                  <div key={idx} className="rounded-xl border bg-white p-3">
                                    <div className="text-sm font-semibold text-gray-900">Rule #{idx + 1}</div>

                                    <div className="mt-1 text-xs text-gray-600">
                                      Min Qty:{" "}
                                      <span className="font-semibold">{p.min_quantity?.[idx] ?? "—"}</span>
                                    </div>

                                    <div className="mt-1 text-xs text-gray-600">
                                      Discounted: <span className="font-semibold">₹{price}</span>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveDiscount(
                                          p.product_id,
                                          idx,
                                          p.min_quantity?.[idx],
                                          p.sizes?.[idx] ?? p.sizes?.[0] ?? ""
                                        )
                                      }
                                      className="mt-3 w-full rounded-xl bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700"
                                    >
                                      Remove This Rule
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination />
        </div>

        <div className="mt-3 text-xs text-gray-500">
          Note: If your backend expects <code>$_POST</code> instead of JSON, tell me and I will convert requests to
          URL-encoded.
        </div>
      </div>
    </div>
  );
}

export default ManageDiscounts;
